import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { StaffRail } from '@/components/layout/StaffRail';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, LogOut, Building, MapPin, Phone, Pencil, Trash2, RefreshCw, Copy } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_BRANCH = { name: '', address: '', city: '', phone: '', currency: 'USD', tax_rate: 0.08, is_active: true };
const CURRENCY_OPTIONS = [
  { code: 'USD', label: 'USD · US Dollar' },
  { code: 'EUR', label: 'EUR · Euro' },
  { code: 'GBP', label: 'GBP · British Pound' },
  { code: 'XAF', label: 'XAF · CFA (Central Africa)' },
  { code: 'XOF', label: 'XOF · CFA (West Africa)' },
];

export default function BranchesPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [branches, setBranches] = useState([]);
  const [tables, setTables] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [showAddTable, setShowAddTable] = useState({ open: false, branchId: '' });
  const [tableForm, setTableForm] = useState({ table_number: '', seats: 4 });
  const [form, setForm] = useState(EMPTY_BRANCH);
  // Edit dialogs / confirms
  const [editBranch, setEditBranch] = useState(null);
  const [editTable, setEditTable] = useState(null);
  const [confirmBranchDelete, setConfirmBranchDelete] = useState(null);
  const [confirmTableDelete, setConfirmTableDelete] = useState(null);
  const [confirmRegenQR, setConfirmRegenQR] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/branches');
      setBranches(data || []);
      const tabs = {};
      for (const b of data) {
        try {
          const r = await api.get(`/branches/${b.id}/tables`);
          tabs[b.id] = r.data || [];
        } catch (e) { tabs[b.id] = []; }
      }
      setTables(tabs);
    } catch (e) { toast.error('Failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createBranch = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try {
      await api.post('/branches', { ...form, tax_rate: Number(form.tax_rate) });
      toast.success('Branch created');
      setShowAdd(false); setForm(EMPTY_BRANCH);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const saveBranchEdit = async () => {
    try {
      const { id, ...rest } = editBranch;
      await api.patch(`/branches/${id}`, { ...rest, tax_rate: Number(rest.tax_rate) });
      toast.success('Branch updated');
      setEditBranch(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const doBranchDelete = async () => {
    if (!confirmBranchDelete) return;
    try {
      await api.delete(`/branches/${confirmBranchDelete.id}`);
      toast.success('Branch removed');
      setConfirmBranchDelete(null);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const createTable = async () => {
    try {
      await api.post(`/branches/${showAddTable.branchId}/tables`, { table_number: tableForm.table_number, seats: Number(tableForm.seats) });
      toast.success('Table created');
      setShowAddTable({ open: false, branchId: '' });
      setTableForm({ table_number: '', seats: 4 });
      load();
    } catch (e) { toast.error('Failed'); }
  };
  const saveTableEdit = async () => {
    try {
      const { id, branch_id, ...rest } = editTable;
      await api.patch(`/branches/${branch_id}/tables/${id}`, { ...rest, seats: Number(rest.seats) });
      toast.success('Table updated');
      setEditTable(null);
      load();
    } catch (e) { toast.error('Failed'); }
  };
  const doTableDelete = async () => {
    if (!confirmTableDelete) return;
    try {
      await api.delete(`/branches/${confirmTableDelete.branch_id}/tables/${confirmTableDelete.id}`);
      toast.success('Table removed');
      setConfirmTableDelete(null);
      load();
    } catch (e) { toast.error('Failed'); }
  };
  const doRegenerateQR = async () => {
    if (!confirmRegenQR) return;
    try {
      await api.post(`/branches/${confirmRegenQR.branch_id}/tables/${confirmRegenQR.id}/regenerate-qr`);
      toast.success('QR rotated');
      setConfirmRegenQR(null);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const copyQR = (token) => {
    try { navigator.clipboard.writeText(token); toast.success('QR copied'); } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-background">
      <StaffRail />
      <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3 sm:px-6">
        <div>
          <h1 className="font-display text-lg font-semibold">Branches</h1>
          <p className="text-xs text-muted-foreground">Multi-branch management · {user?.role}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => nav('/server')}>Server</Button>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild><Button data-testid="branches-add-button" size="sm"><Plus className="mr-1 h-4 w-4" /> Add branch</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New branch</DialogTitle><DialogDescription>Add a new restaurant location.</DialogDescription></DialogHeader>
              <div className="grid gap-3">
                <div className="space-y-1.5"><Label>Name</Label><Input data-testid="branches-add-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Address</Label><Input data-testid="branches-add-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>City</Label><Input data-testid="branches-add-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Douala" /></div>
                  <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Currency</Label>
                    <select data-testid="branches-add-currency-select" className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                      {CURRENCY_OPTIONS.map((c) => (<option key={c.code} value={c.code}>{c.label}</option>))}
                    </select>
                  </div>
                  <div className="space-y-1.5"><Label>Tax rate (e.g. 0.08)</Label><Input type="number" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></div>
                </div>
              </div>
              <DialogFooter><Button data-testid="branches-add-submit-button" onClick={createBranch}>Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Button size="sm" variant="ghost" onClick={() => { logout(); }}><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-4 px-4 py-6 sm:px-6">
        {branches.map((b) => (
          <Card key={b.id} data-testid="branches-card">
            <CardHeader className="flex flex-row items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="font-display"><Building className="mr-2 inline h-5 w-5 text-primary" /> {b.name}</CardTitle>
                <p className="mt-1 text-xs text-muted-foreground"><MapPin className="mr-1 inline h-3 w-3" /> {b.address || '—'}{b.city ? ` · ${b.city}` : ''}</p>
                <p className="text-xs text-muted-foreground"><Phone className="mr-1 inline h-3 w-3" /> {b.phone || '—'} · {b.currency} · Tax {Math.round((b.tax_rate || 0) * 100)}%</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={b.is_active ? 'border border-emerald-200 bg-emerald-50 text-emerald-900' : 'border border-rose-200 bg-rose-50 text-rose-900'}>{b.is_active ? 'Active' : 'Inactive'}</Badge>
                <Button data-testid="branches-edit-button" size="icon" variant="ghost" onClick={() => setEditBranch({ ...b })}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button data-testid="branches-delete-button" size="icon" variant="ghost" className="text-destructive" onClick={() => setConfirmBranchDelete(b)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Tables ({(tables[b.id] || []).length})</p>
                <Button data-testid="branches-add-table-button" size="sm" variant="secondary" onClick={() => setShowAddTable({ open: true, branchId: b.id })}><Plus className="mr-1 h-4 w-4" /> Add table</Button>
              </div>
              <ul className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                {(tables[b.id] || []).map((t) => (
                  <li key={t.id} data-testid="branches-table-row" className="rounded border border-border bg-card p-2">
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold">Table {t.table_number}</p>
                        <p className="text-[10px] text-muted-foreground">{t.seats} seats</p>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditTable({ ...t })}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Rotate QR" onClick={() => setConfirmRegenQR(t)}>
                          <RefreshCw className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => setConfirmTableDelete(t)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <button onClick={() => copyQR(t.qr_token)} title="Copy QR token" className="mt-1 flex w-full items-center gap-1 break-all rounded bg-muted px-1.5 py-1 font-mono text-[10px] text-muted-foreground hover:bg-muted/70">
                      <Copy className="h-3 w-3 shrink-0" /> {t.qr_token}
                    </button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
        {!branches.length && <p className="py-6 text-center text-sm text-muted-foreground">No branches yet. Add your first location.</p>}
      </main>

      {/* Add table */}
      <Dialog open={showAddTable.open} onOpenChange={(open) => setShowAddTable({ open, branchId: open ? showAddTable.branchId : '' })}>
        <DialogContent>
          <DialogHeader><DialogTitle>New table</DialogTitle><DialogDescription>Generates a unique QR code for the table.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="space-y-1.5"><Label>Table number / name</Label><Input data-testid="branches-table-number-input" value={tableForm.table_number} onChange={(e) => setTableForm({ ...tableForm, table_number: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Seats</Label><Input type="number" min="1" value={tableForm.seats} onChange={(e) => setTableForm({ ...tableForm, seats: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="branches-table-submit-button" onClick={createTable} disabled={!tableForm.table_number}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit branch */}
      <Dialog open={!!editBranch} onOpenChange={(o) => { if (!o) setEditBranch(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit branch</DialogTitle><DialogDescription>Update branch details.</DialogDescription></DialogHeader>
          {editBranch && (
            <div className="grid gap-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={editBranch.name} onChange={(e) => setEditBranch({ ...editBranch, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Address</Label><Input value={editBranch.address || ''} onChange={(e) => setEditBranch({ ...editBranch, address: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>City</Label><Input data-testid="branches-edit-city-input" value={editBranch.city || ''} onChange={(e) => setEditBranch({ ...editBranch, city: e.target.value })} placeholder="e.g. Douala" /></div>
                <div className="space-y-1.5"><Label>Phone</Label><Input value={editBranch.phone || ''} onChange={(e) => setEditBranch({ ...editBranch, phone: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Currency</Label>
                  <select data-testid="branches-edit-currency-select" className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={editBranch.currency || 'USD'} onChange={(e) => setEditBranch({ ...editBranch, currency: e.target.value })}>
                    {CURRENCY_OPTIONS.map((c) => (<option key={c.code} value={c.code}>{c.label}</option>))}
                  </select>
                </div>
                <div className="space-y-1.5"><Label>Tax rate</Label><Input type="number" step="0.01" value={editBranch.tax_rate} onChange={(e) => setEditBranch({ ...editBranch, tax_rate: e.target.value })} /></div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!editBranch.is_active} onCheckedChange={(v) => setEditBranch({ ...editBranch, is_active: v })} />
                <Label className="!mt-0">Branch is active</Label>
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="branches-edit-save-button" onClick={saveBranchEdit}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit table */}
      <Dialog open={!!editTable} onOpenChange={(o) => { if (!o) setEditTable(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit table</DialogTitle><DialogDescription>Update table number, seat count, or status.</DialogDescription></DialogHeader>
          {editTable && (
            <div className="grid gap-3">
              <div className="space-y-1.5"><Label>Table number / name</Label><Input value={editTable.table_number} onChange={(e) => setEditTable({ ...editTable, table_number: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Seats</Label><Input type="number" min="1" value={editTable.seats} onChange={(e) => setEditTable({ ...editTable, seats: e.target.value })} /></div>
              <div className="flex items-center gap-2">
                <Switch checked={!!editTable.is_active} onCheckedChange={(v) => setEditTable({ ...editTable, is_active: v })} />
                <Label className="!mt-0">Table is active</Label>
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="branches-table-edit-save-button" onClick={saveTableEdit}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm branch delete */}
      <AlertDialog open={!!confirmBranchDelete} onOpenChange={(o) => { if (!o) setConfirmBranchDelete(null); }}>
        <AlertDialogContent data-testid="branches-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete branch {confirmBranchDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The branch will be deactivated and all its tables will also be disabled. Historical orders remain linked. You can’t undo this from the UI — contact engineering to restore.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="branches-delete-confirm-button" onClick={doBranchDelete}>Delete branch</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm table delete */}
      <AlertDialog open={!!confirmTableDelete} onOpenChange={(o) => { if (!o) setConfirmTableDelete(null); }}>
        <AlertDialogContent data-testid="branches-table-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove table {confirmTableDelete?.table_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              The table will be disabled and its QR code will stop working. Past orders remain linked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="branches-table-delete-confirm-button" onClick={doTableDelete}>Remove table</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm regenerate QR */}
      <AlertDialog open={!!confirmRegenQR} onOpenChange={(o) => { if (!o) setConfirmRegenQR(null); }}>
        <AlertDialogContent data-testid="branches-regen-qr-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate QR for table {confirmRegenQR?.table_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              The current QR token will stop working immediately. Reprint and redeploy the new QR before customers arrive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="branches-regen-qr-confirm-button" onClick={doRegenerateQR}>Rotate QR</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
